import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type IntakeRow = {
  id: string;
  project_id: string | null;
  status: string;
  submitted_at: string;
  created_at: string;
  projects?: {
    name?: string | null;
    project_number?: string | null;
  } | null;
};

export default function CRMIntakes() {
  const { toast } = useToast();
  const [rows, setRows] = useState<IntakeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("external_intake_submissions")
          .select("id, project_id, status, submitted_at, created_at, projects(name, project_number)")
          .order("submitted_at", { ascending: false })
          .limit(500);

        if (error) throw error;
        if (mounted) setRows((data as any) ?? []);
      } catch (e: any) {
        console.error("[CRMIntakes] load failed", e);
        toast({
          title: "Fout",
          description: "Kon intakes niet laden",
          variant: "destructive",
        });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [toast]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = (r.projects?.name ?? "").toLowerCase();
      const number = (r.projects?.project_number ?? "").toLowerCase();
      return name.includes(q) || number.includes(q) || r.id.toLowerCase().includes(q);
    });
  }, [rows, query]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>EMS Intakes</CardTitle>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek op project of ID…"
            className="sm:max-w-sm"
          />
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Laden…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground">Geen intakes gevonden.</div>
          ) : (
            <div className="divide-y rounded-md border">
              {filtered.map((r) => (
                <div key={r.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {r.projects?.name ?? "(Project onbekend)"}
                      {r.projects?.project_number ? (
                        <span className="text-muted-foreground"> · {r.projects.project_number}</span>
                      ) : null}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Status: {r.status} · Ingediend: {new Date(r.submitted_at).toLocaleString("nl-NL")}
                    </div>
                  </div>
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/crm/intake/${r.id}`}>Open</Link>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
